/**
 * Modal "Esqueci minha senha" da tela de login.
 * Envia POST /api/auth/forgot-password — resposta neutra + cooldown de 3 min
 * por e-mail (o backend também bloqueia, isto é só o feedback visual).
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import api, { formatApiError } from "../../utils/api";
import { accentBg } from "../../utils/accent";
import { KeyRound, Loader2, MailCheck, Send, ThumbsUp, X, Clock, XCircle } from "lucide-react";

const COOLDOWN_SECONDS = 180;
const STORAGE_KEY = "autem:pwreset:cooldown";

const readCooldowns = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const remainingFor = (email) => {
  const until = readCooldowns()[email.trim().toLowerCase()];
  if (!until) return 0;
  return Math.max(0, Math.ceil((until - Date.now()) / 1000));
};

const startCooldown = (email, seconds) => {
  const all = readCooldowns();
  all[email.trim().toLowerCase()] = Date.now() + seconds * 1000;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
};

const fmtClock = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function ForgotPasswordDialog({ open, onOpenChange, defaultEmail = "" }) {
  const [email, setEmail] = useState(defaultEmail);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  // Tique do contador enquanto o modal está aberto.
  useEffect(() => {
    if (!open) return undefined;
    setCooldown(remainingFor(email));
    const id = setInterval(() => setCooldown(remainingFor(email)), 1000);
    return () => clearInterval(id);
  }, [open, email]);

  const close = (v) => {
    onOpenChange(v);
    if (!v) {
      setSent(false);
      setError("");
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Digite um endereço de e-mail válido.");
      return;
    }
    if (remainingFor(email) > 0) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      startCooldown(email, data?.cooldown_seconds || COOLDOWN_SECONDS);
      setCooldown(remainingFor(email));
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const blocked = cooldown > 0;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="sm:max-w-md p-0 gap-0 overflow-hidden border-0 shadow-2xl flex flex-col [&>button:last-child]:hidden"
        data-testid="forgot-password-dialog"
      >
        <div className="px-6 py-4 text-white flex items-center justify-between shrink-0" style={accentBg()}>
          <div className="flex items-center gap-2.5">
            <KeyRound className="h-5 w-5" />
            <h2 className="text-base font-semibold">Recuperar senha</h2>
          </div>
          <button
            type="button"
            onClick={() => close(false)}
            className="text-white/80 hover:text-white"
            data-testid="forgot-password-close"
          >
            <X size={20} />
          </button>
        </div>

        {sent ? (
          <div className="p-6 text-center" data-testid="forgot-password-success">
            <div className="h-12 w-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-3">
              <MailCheck size={22} />
            </div>
            <p className="text-sm font-semibold text-gray-900">Verifique seu e-mail</p>
            <p className="text-sm text-gray-500 mt-1">
              Se o e-mail informado estiver cadastrado, enviamos um link para redefinir a senha.
              Ele é de uso único e expira em 30 minutos.
            </p>
            {blocked && (
              <p
                className="text-xs text-gray-400 mt-3 inline-flex items-center gap-1.5"
                data-testid="forgot-password-cooldown-note"
              >
                <Clock size={12} />
                Novo pedido liberado em {fmtClock(cooldown)}
              </p>
            )}
            <Button
              className="mt-5 w-full h-10 text-white gap-2"
              style={accentBg()}
              onClick={() => close(false)}
              data-testid="forgot-password-done"
            >
              <ThumbsUp size={16} />
              Entendi
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="p-6">
            <p className="text-sm text-gray-500 mb-4">
              Informe o e-mail cadastrado. Enviaremos um link para você criar uma nova senha.
            </p>
            <Label className="text-gray-700 text-sm font-medium mb-2 block">E-mail</Label>
            <Input
              autoFocus
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11 rounded-lg"
              data-testid="forgot-password-email"
            />
            {error && (
              <p className="text-sm text-red-600 mt-2" data-testid="forgot-password-error">
                {error}
              </p>
            )}
            {blocked && (
              <div
                className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800"
                data-testid="forgot-password-cooldown"
              >
                <Clock size={14} className="mt-0.5 shrink-0" />
                <span>
                  Já enviamos um link para este e-mail há pouco. Você pode pedir outro em{" "}
                  <strong>{fmtClock(cooldown)}</strong> — confira também a caixa de spam.
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 mt-5">
              <Button
                type="button"
                variant="outline"
                className="h-10 flex-1 gap-2"
                onClick={() => close(false)}
                data-testid="forgot-password-cancel"
              >
                <XCircle size={16} />
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading || blocked}
                className="h-10 flex-1 text-white gap-2"
                style={accentBg()}
                data-testid="forgot-password-submit"
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : blocked ? (
                  <>
                    <Clock size={16} />
                    {fmtClock(cooldown)}
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Enviar link
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
